import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShoppingCart, Plus, Search, Eye, Edit, Trash2, Clock, CheckCircle2, DollarSign, X } from 'lucide-react';

export default function OrdersPage() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ customerName: '', items: '', totalAmount: '', status: 'pending' });

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/orders');
      if (response.data.success) {
        const data = response.data.data;
        setOrders(Array.isArray(data) ? data : (data?.items || []));
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/orders', { ...form, totalAmount: parseFloat(form.totalAmount) || 0 });
      setShowModal(false);
      setForm({ customerName: '', items: '', totalAmount: '', status: 'pending' });
      loadOrders();
    } catch (error: any) { alert(error.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this order?')) return;
    try { await api.delete(`/api/orders/${id}`); loadOrders(); } catch (error) { console.error(error); }
  };

  const filteredOrders = (orders || []).filter((o: any) => (o.orderNumber || '').toLowerCase().includes(search.toLowerCase()) || (o.customerName || '').toLowerCase().includes(search.toLowerCase()));

  const stats = {
    total: (orders || []).length,
    pending: (orders || []).filter((o: any) => o.status === 'pending').length,
    completed: (orders || []).filter((o: any) => o.status === 'completed').length,
    totalAmount: (orders || []).reduce((sum: number, o: any) => sum + (o.totalAmount || 0), 0)
  };

  const getStatusStyle = (status: string) => {
    switch (status) { case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200'; case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200'; case 'cancelled': return 'bg-rose-50 text-rose-700 border-rose-200'; default: return 'bg-gray-50 text-gray-700 border-gray-200'; }
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white transition';

  return (
    <div className='space-y-6' dir='rtl'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center gap-3'>
            <div className='w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl flex items-center justify-center'><ShoppingCart className='w-6 h-6 text-white' /></div>
            <span>Orders Management</span>
          </h1>
          <p className='text-gray-500 mt-2'>Manage customer orders</p>
        </div>
        <button onClick={() => setShowModal(true)} className='flex items-center gap-2 bg-gradient-to-l from-rose-600 to-pink-700 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition'><Plus className='w-5 h-5' />New Order</button>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card className='border-0 shadow-lg'><CardContent className='p-6'><div className='w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center mb-2'><ShoppingCart className='w-5 h-5 text-rose-600' /></div><p className='text-sm text-gray-500'>Total Orders</p><p className='text-2xl font-bold'>{stats.total}</p></CardContent></Card>
        <Card className='border-0 shadow-lg'><CardContent className='p-6'><div className='w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-2'><Clock className='w-5 h-5 text-amber-600' /></div><p className='text-sm text-gray-500'>Pending</p><p className='text-2xl font-bold'>{stats.pending}</p></CardContent></Card>
        <Card className='border-0 shadow-lg'><CardContent className='p-6'><div className='w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-2'><CheckCircle2 className='w-5 h-5 text-emerald-600' /></div><p className='text-sm text-gray-500'>Completed</p><p className='text-2xl font-bold'>{stats.completed}</p></CardContent></Card>
        <Card className='border-0 shadow-lg'><CardContent className='p-6'><div className='w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-2'><DollarSign className='w-5 h-5 text-purple-600' /></div><p className='text-sm text-gray-500'>Total Amount</p><p className='text-2xl font-bold'>{stats.totalAmount.toLocaleString()} EGP</p></CardContent></Card>
      </div>

      <Card className='border-0 shadow-lg'>
        <CardHeader className='pb-4'>
          <div className='relative'><Search className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' /><input type='text' placeholder='Search orders...' value={search} onChange={(e) => setSearch(e.target.value)} className='w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-rose-500' /></div>
        </CardHeader>
        <CardContent>
          {loading ? (<div className='p-12 text-center'><div className='animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto'></div></div>) : filteredOrders.length === 0 ? (<div className='p-12 text-center'><ShoppingCart className='w-16 h-16 text-gray-300 mx-auto mb-4' /><h3 className='text-lg font-semibold'>No orders found</h3></div>) : (
            <div className='overflow-x-auto'><table className='w-full'>
              <thead className='bg-gray-50 border-b border-gray-100'><tr><th className='text-right py-4 px-5 text-sm font-semibold'>Order #</th><th className='text-right py-4 px-5 text-sm font-semibold'>Customer</th><th className='text-right py-4 px-5 text-sm font-semibold'>Items</th><th className='text-right py-4 px-5 text-sm font-semibold'>Amount</th><th className='text-right py-4 px-5 text-sm font-semibold'>Status</th><th className='text-right py-4 px-5 text-sm font-semibold'>Actions</th></tr></thead>
              <tbody>
                {filteredOrders.map((order: any) => (
                  <tr key={order.id} className='border-b border-gray-50 hover:bg-gray-50/50'>
                    <td className='py-4 px-5'><span className='font-mono font-semibold text-rose-600'>{order.orderNumber}</span></td>
                    <td className='py-4 px-5 font-semibold'>{order.customerName}</td>
                    <td className='py-4 px-5 text-sm text-gray-600'>{order.items}</td>
                    <td className='py-4 px-5 font-bold'>{(order.totalAmount || 0).toLocaleString()} EGP</td>
                    <td className='py-4 px-5'><span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusStyle(order.status)}`}>{order.status}</span></td>
                    <td className='py-4 px-5'><div className='flex gap-1'><button className='p-2 hover:bg-rose-50 rounded-lg text-gray-500 hover:text-rose-600'><Eye className='w-4 h-4' /></button><button className='p-2 hover:bg-amber-50 rounded-lg text-gray-500 hover:text-amber-600'><Edit className='w-4 h-4' /></button><button onClick={() => handleDelete(order.id)} className='p-2 hover:bg-rose-50 rounded-lg text-gray-500 hover:text-rose-600'><Trash2 className='w-4 h-4' /></button></div></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </CardContent>
      </Card>

      {showModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-2xl'>
            <CardHeader><div className='flex items-center justify-between'><CardTitle>New Order</CardTitle><button onClick={() => setShowModal(false)}><X className='w-5 h-5' /></button></div></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div><Label>Customer Name *</Label><Input value={form.customerName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, customerName: e.target.value })} required className={inputClass} /></div>
                  <div><Label>Total Amount *</Label><Input type='number' value={form.totalAmount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, totalAmount: e.target.value })} required className={inputClass} /></div>
                  <div className='col-span-2'><Label>Items</Label><Input value={form.items} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, items: e.target.value })} className={inputClass} placeholder='Item 1, Item 2...' /></div>
                  <div><Label>Status</Label><select value={form.status} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setForm({ ...form, status: e.target.value })} className={inputClass}><option value='pending'>Pending</option><option value='completed'>Completed</option><option value='cancelled'>Cancelled</option></select></div>
                </div>
                <div className='flex gap-3 pt-4'><Button type='button' variant='outline' onClick={() => setShowModal(false)} className='flex-1'>Cancel</Button><Button type='submit' className='flex-1 bg-gradient-to-l from-rose-600 to-pink-700'>Create Order</Button></div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}